import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, faqLd, breadcrumbLd, webAppLd } from "../components/Seo";
import { COUNTRIES } from "../lib/countries";
import { realityCheck } from "../lib/api";
import { track } from "../lib/analytics";
import {
  ShieldWarning,
  CloudRain,
  Users,
  CurrencyInr,
  CheckCircle,
  XCircle,
  Warning,
  ThermometerSimple,
  Drop,
  Lightning,
  ArrowRight,
  Sparkle,
  MapPin,
  Calendar,
  Crosshair,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const INTERESTS = [
  { key: "everything", label: "Everything", emoji: "✨" },
  { key: "culture", label: "Culture", emoji: "🏛️" },
  { key: "nightlife", label: "Nightlife", emoji: "🍸" },
  { key: "food", label: "Food", emoji: "🍜" },
  { key: "chill", label: "Chill", emoji: "🌴" },
  { key: "adventure", label: "Adventure", emoji: "🏔️" },
];

const CROWD_META = {
  low: { color: "#3DB68B", label: "Calm" },
  moderate: { color: "#F4B942", label: "Moderate" },
  high: { color: "#FF7E4D", label: "Busy" },
  peak: { color: "#E0457B", label: "Peak crush" },
};

const WEATHER_META = {
  low: { color: "#3DB68B", label: "Smooth sailing" },
  moderate: { color: "#F4B942", label: "Some risk" },
  high: { color: "#E0457B", label: "High risk" },
};

const COST_META = {
  cheap: { color: "#3DB68B", label: "Cheap month" },
  mid: { color: "#2A7FB8", label: "Mid-season" },
  peak: { color: "#E0457B", label: "Peak prices" },
};

// Returns YYYY-MM-DD offset days from today.
const isoOffset = (offsetDays) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

export default function RealityCheck() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("thailand");
  const [start, setStart] = useState(isoOffset(30));
  const [end, setEnd] = useState(isoOffset(36));
  const [interests, setInterests] = useState(["everything"]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleInterest = (k) => {
    setInterests((prev) => {
      // "everything" is exclusive — selecting it clears the rest, and vice-versa.
      if (k === "everything") return prev.includes("everything") ? [] : ["everything"];
      const next = prev.filter((x) => x !== "everything");
      return next.includes(k) ? next.filter((x) => x !== k) : [...next, k];
    });
  };

  const days =
    start && end ? Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1) : 0;

  const run = async () => {
    if (!slug || !start || !end) {
      toast.error("Pick a destination and both dates");
      return;
    }
    if (new Date(end) < new Date(start)) {
      toast.error("End date must be on or after start date");
      return;
    }
    if (!interests.length) {
      toast.error("Pick at least one interest");
      return;
    }
    track("reality_check_started", { country: slug, interests, days });
    setLoading(true);
    setResult(null);
    try {
      const data = await realityCheck({
        country_slug: slug,
        start_date: start,
        end_date: end,
        interests,
      });
      setResult(data);
      track("reality_check_completed", {
        country: slug,
        verdict_score: data?.verdict?.score,
        worth_it: data?.verdict?.worth_it,
      });
      setTimeout(
        () =>
          document
            .getElementById("rc-result")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        180,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run the reality check. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="Trip reality check — is it worth visiting this month?"
        description="Honest, AI-powered reality check on your destination — crowds, scams, weather risk, cost vs other months, and a final 'is it worth it' verdict for your exact dates."
        path="/reality-check"
        jsonLd={[
          webAppLd({
            name: "Flycher Trip Reality Check",
            description: "AI-powered honest verdict on whether to visit a country in a given month.",
            path: "/reality-check",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Reality Check", path: "/reality-check" },
          ]),
          faqLd([
            { question: "Is Thailand worth visiting in July?", answer: "July is monsoon season in Thailand with heavy rain in most regions. Crowds are lower and prices drop 20-30%, but outdoor activities and island-hopping are limited. November-February is the sweet spot." },
            { question: "When is the best time to visit Bali?", answer: "April-June and September-October are ideal — sunny, less crowded, and ~25% cheaper than peak (July-August, Christmas-New Year). Avoid January-March due to heavy monsoon." },
            { question: "Is Japan crowded during cherry blossom season?", answer: "Yes, late March to early April is peak. Tokyo, Kyoto, Osaka see 2-3× normal tourist density. Book stays 3-4 months ahead. November autumn colors are equally beautiful and 40% less crowded." },
            { question: "What are common tourist scams in [country]?", answer: "Each destination has its own scams — Bangkok tuk-tuk gem-store, Bali fake cab meters, Hanoi shoe-shine forced sales, Tokyo touts in Roppongi. Our reality check lists the top 4-5 active scams per country with how-to-dodge tips." },
          ]),
        ]}
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Trip reality check</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          Should I actually go,
          <br />
          <span className="italic" style={{ color: "#E0457B" }}>
            this month?
          </span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          A brutally honest, AI-generated read on your destination for your <strong>exact</strong>{" "}
          travel window — crowds, weather, scams, cost-vs-offseason, and a verdict score so you don't
          burn your PTO on a typhoon month.
        </p>

        {/* Form */}
        <div
          data-testid="rc-form"
          className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          <label className="md:col-span-6 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Destination
            </span>
            <select
              data-testid="rc-country"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="select-base h-12 text-base"
            >
              {COUNTRIES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="md:col-span-3 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">From date</span>
            <input
              type="date"
              data-testid="rc-start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="input-base h-12 text-base"
            />
          </label>
          <label className="md:col-span-3 flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">To date</span>
            <input
              type="date"
              data-testid="rc-end"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="input-base h-12 text-base"
            />
          </label>

          <div className="md:col-span-12">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">
              What do you want to cover? <span className="text-slate-400 normal-case tracking-normal">(multi-select)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((opt) => {
                const active = interests.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`rc-interest-${opt.key}`}
                    onClick={() => toggleInterest(opt.key)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border transition ${
                      active
                        ? "text-white border-transparent shadow-sm"
                        : "border-slate-200 bg-white text-slate-700 hover:border-[#E0457B]/40"
                    }`}
                    style={active ? { background: "#E0457B" } : undefined}
                  >
                    <span>{opt.emoji}</span> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-12 flex items-center justify-between flex-wrap gap-3">
            {days > 0 && (
              <p className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">
                {days} {days === 1 ? "day" : "days"} window
              </p>
            )}
            <button
              type="button"
              onClick={run}
              disabled={loading}
              data-testid="rc-run-btn"
              className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-lg shadow-pink-200/60 disabled:opacity-50"
              style={{ background: "#E0457B" }}
            >
              <Crosshair size={16} weight="bold" />
              {loading ? "Reading the room…" : "Run reality check"}
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#E0457B] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-slate-600 font-mono uppercase tracking-wider">
              Checking crowds · scams · weather · cost…
            </p>
          </div>
        )}

        {result && !loading && <Result data={result} slug={slug} navigate={navigate} />}
      </main>
      <Footer />
    </div>
  );
}

function Result({ data, slug, navigate }) {
  const v = data?.verdict || {};
  const crowd = data?.crowd || {};
  const weather = data?.weather || {};
  const cost = data?.cost || {};
  const crowdMeta = CROWD_META[crowd.level] || CROWD_META.moderate;
  const weatherMeta = WEATHER_META[weather.risk_level] || WEATHER_META.moderate;
  const costMeta = COST_META[cost.tier] || COST_META.mid;
  const verdictColor = v.worth_it ? "#3DB68B" : "#E0457B";

  return (
    <section id="rc-result" data-testid="rc-result" className="mt-12 space-y-6">
      {/* VERDICT HERO */}
      <div
        className="rounded-2xl p-6 sm:p-8 text-white shadow-xl"
        style={{
          background: v.worth_it
            ? "linear-gradient(135deg, #3DB68B 0%, #2A7FB8 100%)"
            : "linear-gradient(135deg, #E0457B 0%, #7C3AED 100%)",
        }}
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/15 backdrop-blur">
            {v.worth_it ? (
              <CheckCircle size={32} weight="fill" />
            ) : (
              <XCircle size={32} weight="fill" />
            )}
          </div>
          <div className="flex-1 min-w-[200px]">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/80">
              Verdict · {data.country} · {data.month}
            </span>
            <h2 className="font-serif text-3xl sm:text-5xl mt-1 leading-tight">
              {v.headline || (v.worth_it ? "Go for it." : "Maybe pick another month.")}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/95 leading-relaxed">
              {v.one_liner}
            </p>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/70">
              Score
            </span>
            <div className="font-serif text-5xl sm:text-6xl font-bold">{v.score ?? "—"}</div>
            <span className="text-[10px] text-white/70">/ 10</span>
          </div>
        </div>

        {v.suggest_alt_month && v.alt_month && (
          <div className="mt-5 rounded-xl bg-white/15 backdrop-blur p-4 flex items-start gap-3">
            <Sparkle size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-white/80">
                Better month
              </p>
              <p className="text-sm font-semibold mt-0.5">
                Try <span className="underline decoration-2 underline-offset-2">{v.alt_month}</span>{" "}
                instead.
              </p>
              <p className="text-xs text-white/90 mt-1">{v.alt_reason}</p>
            </div>
          </div>
        )}
      </div>

      {/* SIGNAL CARDS — crowd / weather / cost */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="rc-signals">
        <SignalCard
          testid="rc-signal-crowd"
          Icon={Users}
          label="Crowds"
          tone={crowdMeta.color}
          heading={crowdMeta.label}
          score={crowd.score}
          scoreMax={10}
          summary={crowd.summary}
        />
        <SignalCard
          testid="rc-signal-weather"
          Icon={CloudRain}
          label="Weather"
          tone={weatherMeta.color}
          heading={weatherMeta.label}
          summary={weather.summary}
          footer={
            <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-600 font-mono">
              <span className="inline-flex items-center gap-1">
                <ThermometerSimple size={13} weight="duotone" />
                {weather.avg_temp_c?.low ?? "—"}–{weather.avg_temp_c?.high ?? "—"}°C
              </span>
              <span className="inline-flex items-center gap-1">
                <Drop size={13} weight="fill" />
                {weather.rain_chance_pct ?? "—"}% rain
              </span>
            </div>
          }
        />
        <SignalCard
          testid="rc-signal-cost"
          Icon={CurrencyInr}
          label="Cost vs offseason"
          tone={costMeta.color}
          heading={costMeta.label}
          summary={cost.note}
          footer={
            cost.vs_offseason_pct != null && (
              <p className="text-[11px] font-mono mt-2" style={{ color: costMeta.color }}>
                {cost.vs_offseason_pct > 0
                  ? `+${cost.vs_offseason_pct}% vs cheapest month`
                  : cost.vs_offseason_pct < 0
                  ? `${cost.vs_offseason_pct}% (cheaper than average)`
                  : "≈ average pricing"}
              </p>
            )
          }
        />
      </div>

      {/* BUSY SPOTS */}
      {crowd.busy_spots?.length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={18} weight="duotone" color={crowdMeta.color} />
            <span className="kicker">— Crowd hotspots this month</span>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2 mt-1">
            {crowd.busy_spots.map((s, i) => (
              <li
                key={i}
                className="text-sm text-slate-700 flex items-start gap-2 leading-relaxed"
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                  style={{ background: crowdMeta.color }}
                />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* WEATHER HAZARDS */}
      {weather.hazards?.length > 0 && (
        <div
          className="rounded-2xl border p-5"
          style={{
            background: weatherMeta.color + "0F",
            borderColor: weatherMeta.color + "33",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Warning size={18} weight="fill" color={weatherMeta.color} />
            <span className="kicker">— Weather hazards to plan for</span>
          </div>
          <ul className="space-y-2 mt-1">
            {weather.hazards.map((h, i) => (
              <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                <Lightning size={14} weight="fill" color={weatherMeta.color} className="mt-1 flex-shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* SCAMS */}
      {data.scams?.length > 0 && (
        <div data-testid="rc-scams">
          <div className="flex items-center gap-2 mb-3">
            <ShieldWarning size={20} weight="duotone" color="#FF7E4D" />
            <span className="kicker">— Top scams to dodge in {data.country}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.scams.map((s, i) => (
              <article
                key={i}
                data-testid={`rc-scam-${i}`}
                className="rounded-2xl bg-white border border-slate-200 p-4 hover:border-[#FF7E4D]/40 transition-colors"
              >
                <h3 className="font-serif text-lg text-slate-900 leading-tight">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.description}</p>
                <div className="mt-3 rounded-xl bg-[#FFF1E8] border border-[#FF7E4D]/15 px-3 py-2">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF7E4D]">
                    How to dodge
                  </p>
                  <p className="text-xs text-slate-800 mt-0.5 leading-relaxed">{s.how_to_avoid}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 pt-2">
        <button
          type="button"
          data-testid="rc-build-itinerary"
          onClick={() => {
            track("rc_to_itinerary_clicked", { country: slug });
            const params = new URLSearchParams({
              start: data.start_date,
              end: data.end_date,
            });
            navigate(`/itinerary/${slug}?${params.toString()}`);
          }}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md"
          style={{ background: "#FF7E4D" }}
        >
          <Sparkle size={14} weight="fill" />
          Build an itinerary for these dates
          <ArrowRight size={12} weight="bold" />
        </button>
        <button
          type="button"
          data-testid="rc-see-budget"
          onClick={() => navigate("/budget")}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-[#3DB68B] hover:text-[#3DB68B]"
        >
          <Calendar size={14} weight="duotone" />
          Estimate the budget
          <ArrowRight size={12} weight="bold" />
        </button>
      </div>

      <p className="text-[11px] text-slate-400 font-mono text-center pt-4">
        AI-generated reality check · cross-check against official advisories before booking.
      </p>
    </section>
  );
}

function SignalCard({ testid, Icon, label, tone, heading, score, scoreMax = 10, summary, footer }) {
  return (
    <article
      data-testid={testid}
      className="rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg grid place-items-center"
            style={{ background: tone + "1A", color: tone }}
          >
            <Icon size={16} weight="duotone" />
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
            {label}
          </span>
        </div>
        {score != null && (
          <span className="text-[11px] font-mono" style={{ color: tone }}>
            {score}/{scoreMax}
          </span>
        )}
      </div>
      <p className="font-serif text-xl mt-2" style={{ color: tone }}>
        {heading}
      </p>
      {summary && <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{summary}</p>}
      {footer}
    </article>
  );
}
